import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  addDoc,
  collection,
  query,
  where,
  getDocs,
  arrayUnion,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "./firebase";
import {
  mentorFeedbackTitle,
  sessionReminderCopy,
  weeklyCheckinCopy,
  lessonCompletedCopy,
  programMilestoneCopy,
  lessonAckTitle,
} from "./notificationCopy";

// ─── Enrollment ─────────────────────────────────────────────────────────────

/**
 * Enroll a student in a program.
 * Creates the enrollment doc and adds the programId to the user's profile.
 * mentorId (optional): acompañante DE ESTA ACTIVIDAD, no del usuario en general —
 * ver project-log/ARCHIVE.md, "Sistema unificado de roles y acompañamiento", Decisión 2.
 */
export async function enrollInProgram(userId, programId, { mentorId } = {}) {
  const enrollId = `${userId}_${programId}`;
  const enrollRef = doc(db, "enrollments", enrollId);
  const snap = await getDoc(enrollRef);
  if (snap.exists()) return; // already enrolled

  await setDoc(enrollRef, {
    userId,
    programId,
    enrolledAt: serverTimestamp(),
    status: "active",
    completedLessons: [],
    percentComplete: 0,
    mentorId: mentorId || null,
  });

  await updateDoc(doc(db, "users", userId), {
    enrolledPrograms: arrayUnion(programId),
  });
}

/**
 * Set or change the accompaniment mentor for a specific enrollment (activity-scoped,
 * not the global users/{uid}.mentorId). Pass null to unassign.
 */
export async function setEnrollmentMentor(userId, programId, mentorId) {
  await updateDoc(doc(db, "enrollments", `${userId}_${programId}`), {
    mentorId: mentorId || null,
  });
}

/**
 * Get the enrollment document for a user/program pair.
 * Returns null if not enrolled.
 */
export async function getEnrollment(userId, programId) {
  const snap = await getDoc(doc(db, "enrollments", `${userId}_${programId}`));
  return snap.exists() ? snap.data() : null;
}

/**
 * Get all enrollments for a student (for dashboard).
 */
export async function getStudentEnrollments(userId) {
  const q = query(collection(db, "enrollments"), where("userId", "==", userId));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// ─── Progress ────────────────────────────────────────────────────────────────

/**
 * Mark a lesson as completed and recalculate percentComplete.
 * totalLessons: total number of lessons in the program (pass from program data).
 * S14b: además de marcar la lección, avisa al mentor (si lo hay) y detecta si el
 * programa completo llegó a 100% para el aviso de hito aparte. mentorInfo es opcional
 * — llamadas viejas sin el parámetro siguen funcionando, solo sin avisar a nadie.
 */
export async function markLessonComplete(userId, programId, lessonId, totalLessons, mentorInfo) {
  const enrollRef = doc(db, "enrollments", `${userId}_${programId}`);
  const snap = await getDoc(enrollRef);
  if (!snap.exists()) return;

  const data = snap.data();
  const completed = data.completedLessons || [];
  if (completed.includes(lessonId)) return; // already done

  const newCompleted = [...completed, lessonId];
  const percent = Math.round((newCompleted.length / totalLessons) * 100);

  await updateDoc(enrollRef, {
    completedLessons: arrayUnion(lessonId),
    percentComplete: percent,
    status: percent >= 100 ? "completed" : "active",
  });

  if (mentorInfo?.mentorId) {
    const { mentorId, studentName, lessonTitle, programTitle, moduleId, language } = mentorInfo;
    const lang = language === "en" ? "en" : "es";
    try {
      const lc = lessonCompletedCopy(lang, { studentName, lessonTitle });
      await addDoc(collection(db, "notifications"), {
        userId: mentorId,
        type: "lesson_completed",
        language: lang,
        payload: { ...lc, data: { programId, lessonId, moduleId, studentId: userId } },
        status: "pending",
        readAt: null,
        createdAt: serverTimestamp(),
      });
      if (percent >= 100) {
        const pm = programMilestoneCopy(lang, { studentName, programTitle });
        await addDoc(collection(db, "notifications"), {
          userId: mentorId,
          type: "program_milestone",
          language: lang,
          payload: { ...pm, data: { programId, studentId: userId } },
          status: "pending",
          readAt: null,
          createdAt: serverTimestamp(),
        });
      }
    } catch (err) {
      console.warn("[study-service] no se pudo crear el aviso de lección/hito:", err?.code);
    }
  }
}

/**
 * S14b: el mentor "reconoce" una lección terminada — mensaje opcional visible al
 * discípulo + nota privada opcional para sí mismo. Ambos campos son independientes;
 * cualquiera puede quedar vacío. Solo dispara lesson_ack si studentMessage no está vacío
 * (mismo criterio que mentorFeedback: un mensaje real vale un correo, un formulario
 * vacío no).
 */
export async function acknowledgeLessonCompletion({
  studentId,
  programId,
  lessonId,
  moduleId,
  mentorUid,
  studentMessage,
  privateNote,
  language,
}) {
  await setDoc(
    doc(db, "lessonFeedback", `${studentId}_${programId}_${lessonId}`),
    { studentId, programId, lessonId, moduleId, mentorUid, studentMessage: studentMessage || "", at: serverTimestamp() },
    { merge: true }
  );

  if (privateNote?.trim()) {
    await setDoc(
      doc(db, "mentorNotes", `${mentorUid}_${studentId}_${programId}_${lessonId}`),
      { mentorId: mentorUid, studentId, programId, lessonId, text: privateNote, updatedAt: serverTimestamp() },
      { merge: true }
    );
  }

  if (studentMessage?.trim()) {
    const lang = language === "en" ? "en" : "es";
    await addDoc(collection(db, "notifications"), {
      userId: studentId,
      type: "lesson_ack",
      language: lang,
      payload: { title: lessonAckTitle(lang), body: studentMessage },
      status: "pending",
      readAt: null,
      createdAt: serverTimestamp(),
    });
  }
}

// ─── Responses ───────────────────────────────────────────────────────────────

/**
 * Save (or overwrite) a student's answer to a question.
 * language: 'es' | 'en' — the language the student was using when answering.
 */
export async function saveResponse(userId, programId, lessonId, questionId, answer, moduleId, language = "es") {
  const qId = questionId.toLowerCase();
  const responseId = `${userId}_${programId}_${lessonId}_${qId}`;
  await setDoc(
    doc(db, "responses", responseId),
    {
      userId,
      programId,
      moduleId: moduleId || null,
      lessonId,
      questionId: qId,
      answer,
      language,
      savedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

/**
 * Get all responses for a student within a specific lesson.
 * Uses only userId filter to avoid composite index requirement,
 * then filters client-side by programId + lessonId.
 */
export async function getLessonResponses(userId, programId, lessonId) {
  const q = query(
    collection(db, "responses"),
    where("userId", "==", userId)
  );
  const snap = await getDocs(q);
  const map = {};
  snap.docs.forEach((d) => {
    const data = d.data();
    if (data.programId === programId && data.lessonId === lessonId) {
      map[data.questionId] = { id: d.id, ...data };
    }
  });
  return map;
}

/**
 * Get all responses for a student in a program (for mentor view).
 */
export async function getProgramResponses(userId, programId) {
  const q = query(
    collection(db, "responses"),
    where("userId", "==", userId)
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((d) => d.programId === programId);
}

/**
 * Get ALL responses for a user, across every program — used to compute the
 * global "unseen mentor feedback" count and, from the mentor side, to detect
 * responses awaiting review beyond a single program.
 */
export async function getAllUserResponses(userId) {
  const q = query(collection(db, "responses"), where("userId", "==", userId));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// ─── Mentor feedback ─────────────────────────────────────────────────────────

/**
 * Save or update mentor feedback on a specific response document.
 * studentId/language alimentan el bus de notificaciones (Fase 3,
 * notificaciones-sistema.md): dispara un aviso al discípulo cuando su mentor le
 * responde. Ambos ya están disponibles en el objeto `response` que trae el caller
 * (mentor/[studentId].js) — no hace falta una lectura extra para conseguirlos.
 * Si no se pasan (llamadas viejas), la notificación simplemente no se crea; el
 * feedback en sí se guarda igual.
 */
export async function saveMentorFeedback(responseDocId, feedbackText, studentId, language) {
  await updateDoc(doc(db, "responses", responseDocId), {
    mentorFeedback: {
      text: feedbackText,
      at: serverTimestamp(),
    },
  });

  if (studentId) {
    const lang = language === "en" ? "en" : "es";
    // El cuerpo es la respuesta real que escribió el mentor — no hay copy que centralizar
    // ahí, solo el título (notificationCopy.js).
    const title = mentorFeedbackTitle(lang);
    await addDoc(collection(db, "notifications"), {
      userId: studentId,
      type: "mentor_feedback",
      language: lang,
      payload: { title, body: feedbackText },
      status: "pending",
      readAt: null,
      createdAt: serverTimestamp(),
    });
  }
}

/**
 * Mark a response's mentor feedback as seen by the student who owns it.
 * Uses dot-notation so only the nested field is touched, not the whole map.
 */
export async function markResponseFeedbackSeen(responseDocId) {
  await updateDoc(doc(db, "responses", responseDocId), {
    "mentorFeedback.seenByStudent": true,
  });
}

// ─── Mentor students ─────────────────────────────────────────────────────────

/**
 * Get all students assigned to a mentor (by mentorId field on user profile).
 */
export async function getMentorStudents(mentorId) {
  const q = query(collection(db, "users"), where("mentorId", "==", mentorId));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ uid: d.id, ...d.data() }));
}

/**
 * Get all enrollments where this mentor is the activity-scoped companion
 * (enrollments.mentorId), regardless of the student's global mentorId. Needed so a
 * FRUTO companion who isn't the student's general mentor still discovers them in
 * their own /mentor/ panel — see project-log/ARCHIVE.md, "Sistema unificado de roles y
 * acompañamiento".
 */
export async function getMentorFrutoStudents(mentorId) {
  const q = query(collection(db, "enrollments"), where("mentorId", "==", mentorId));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// ─── Mentor directory ────────────────────────────────────────────────────────
// Directorio mínimo ({uid, displayName}) de mentores/admins, sincronizado por
// admin-service.js. Permite que un discípulo elija acompañante FRUTO sin exponer
// email ni otros campos de users/ — ver Decisión 1 del pre-plan.

/**
 * List all entries in the mentor directory (for the FRUTO companion picker).
 */
export async function getMentorDirectory() {
  const snap = await getDocs(collection(db, "mentorDirectory"));
  return snap.docs.map((d) => d.data());
}

/**
 * Get a single mentor directory entry by uid (to show "acompañado por {name}").
 * Returns null if not found (e.g. mentor was demoted after this enrollment).
 */
export async function getMentorDirectoryEntry(uid) {
  if (!uid) return null;
  const snap = await getDoc(doc(db, "mentorDirectory", uid));
  return snap.exists() ? snap.data() : null;
}

// ─── FRUTO Pastoral 8h — aprobación de estadios por el mentor ─────────────────
// El curso de 8h no tiene lecciones (ver src/data/programs/fruto-pastoral-8h.js):
// el avance entre Raíz/Tronco/Ramas lo aprueba el mentor en conversación, no una
// lección completada. Se guarda en el mismo doc de enrollments, no en uno nuevo.

export const FRUTO_STAGES = ["raiz", "tronco", "ramas"];

/**
 * Record the mentor's approval of a FRUTO stage transition, with a note of the
 * evidence discussed (per Doc 01: "la diferencia... la detecta el mentor en
 * conversación"). Uses dot-notation so only that stage's entry is touched.
 */
export async function approveFrutoStage(userId, programId, stage, approvedBy, note) {
  const enrollRef = doc(db, "enrollments", `${userId}_${programId}`);
  await updateDoc(enrollRef, {
    [`stageApprovals.${stage}`]: {
      approvedBy,
      approvedAt: serverTimestamp(),
      note: note || "",
    },
  });
}

// ─── FRUTO Pastoral 8h — Semáforo de Gracia (check-in semanal) ────────────────
// Reporte semanal del pastor sobre su Ladrillo: verde (cumplido, produjo algo
// real) / amarillo (intención genuina, falló) / naranja (sin cambio). El criterio
// no es el color sino la honestidad del reporte (Doc 01, cierre del curso).

/**
 * Submit (or overwrite) this week's Semáforo de Gracia check-in.
 * weekOf: "YYYY-MM-DD" of the Monday of that week.
 * mentorId (opcional): el acompañante de la inscripción FRUTO (enrollments/{uid}_fruto-
 * pastoral-8h.mentorId, distinto de profile.mentorId global) — si se pasa, avisa al mentor
 * por el bus (cierre-notificaciones-newsletter.md). Si no se pasa (llamadas viejas o sin
 * mentor asignado a esa inscripción), el check-in se guarda igual, sin aviso.
 */
export async function submitFrutoCheckin(userId, weekOf, color, note, mentorId) {
  const checkinId = `${userId}_${weekOf}`;
  await setDoc(doc(db, "frutoCheckins", checkinId), {
    userId,
    weekOf,
    color,
    note: note || "",
    savedAt: serverTimestamp(),
  });

  if (mentorId) {
    try {
      await addDoc(collection(db, "notifications"), {
        userId: mentorId,
        type: "weekly_checkin",
        language: "es",
        payload: weeklyCheckinCopy({ color }),
        status: "pending",
        readAt: null,
        createdAt: serverTimestamp(),
      });
    } catch (err) {
      console.warn("[study-service] no se pudo crear la notificación de weekly_checkin:", err?.code);
    }
  }
}

/**
 * Get all Semáforo de Gracia check-ins for a pastor, most recent week first.
 */
export async function getFrutoCheckins(userId) {
  const q = query(collection(db, "frutoCheckins"), where("userId", "==", userId));
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (b.weekOf || "").localeCompare(a.weekOf || ""));
}

// ─── "La Semana" — objeto compartido discípulo↔mentor (Bloque H, U4) ─────────
// Ata al mentorId GLOBAL del discípulo (profile.mentorId), no al mentorId por
// inscripción de FRUTO — decisión de alcance de U4, ver
// project-log/sesiones/U4-la-semana.md. No confundir con el Semáforo de Gracia
// de arriba, que es específico de fruto-pastoral-8h.

function sessionDocId(studentId, mentorId, weekOf) {
  return `${studentId}_${mentorId}_${weekOf}`;
}

/**
 * weekOf: "YYYY-MM-DD" of the Monday of that week (getWeekStartLocal in mi-cuenta.js).
 */
export async function getWeekSession(studentId, mentorId, weekOf) {
  const snap = await getDoc(doc(db, "sessions", sessionDocId(studentId, mentorId, weekOf)));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/**
 * Create this week's session doc if it doesn't exist yet. Must run from the student's
 * side — firestore.rules only lets the student create it (create requires
 * request.resource.data.studentId == request.auth.uid), the mentor can only update an
 * existing doc. Call this once when the student's Hoy view renders, so the mentor's
 * "sesión realizada" has something to update even if the student never wrote a topic.
 */
export async function ensureWeekSession(studentId, mentorId, weekOf) {
  const ref = doc(db, "sessions", sessionDocId(studentId, mentorId, weekOf));
  const snap = await getDoc(ref);
  if (snap.exists()) return { id: snap.id, ...snap.data() };
  const data = {
    studentId,
    mentorId,
    weekOf,
    topic: "",
    topicShared: false,
    nextSessionDate: null,
    nextSessionTime: null,
    nextSessionDateSetBy: null,
    completed: false,
    completedAt: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  await setDoc(ref, data);
  return { id: ref.id, ...data };
}

/**
 * Student-side update. `updates` may only touch topic/topicShared/nextSessionDate/
 * nextSessionTime/nextSessionDateSetBy — firestore.rules rejects anything else.
 *
 * Fase 7 de notificaciones-sistema.md: si `updates` trae fecha Y hora juntas (el caller
 * manda el par completo, no solo el campo que cambió — ver handleDateChange/
 * handleTimeChange en mi-cuenta.js), dispara el recordatorio de sesión con .ics para
 * las dos personas. Se re-dispara en cada edición posterior de fecha u hora — no hay
 * deduplicación todavía; es una simplificación consciente, no un descuido.
 */
export async function saveWeekSessionAsStudent(studentId, mentorId, weekOf, updates, language = "es") {
  await setDoc(
    doc(db, "sessions", sessionDocId(studentId, mentorId, weekOf)),
    { ...updates, updatedAt: serverTimestamp() },
    { merge: true }
  );

  if (updates.nextSessionDate && updates.nextSessionTime) {
    const lang = language === "en" ? "en" : "es";
    const { title, body } = sessionReminderCopy(lang, {
      date: updates.nextSessionDate,
      time: updates.nextSessionTime,
    });
    const payload = { title, body, date: updates.nextSessionDate, time: updates.nextSessionTime };
    try {
      await Promise.all([
        addDoc(collection(db, "notifications"), {
          userId: studentId,
          type: "session_reminder",
          language: lang,
          payload,
          status: "pending",
          readAt: null,
          createdAt: serverTimestamp(),
        }),
        addDoc(collection(db, "notifications"), {
          userId: mentorId,
          type: "session_reminder",
          language: lang,
          payload,
          status: "pending",
          readAt: null,
          createdAt: serverTimestamp(),
        }),
      ]);
    } catch (err) {
      console.warn("[study-service] no se pudo crear el recordatorio de sesión:", err?.code);
    }
  }
}

/**
 * Mentor-side update. `updates` may only touch completed/completedAt/nextSessionDate/
 * nextSessionDateSetBy — firestore.rules rejects anything else. The doc must already
 * exist (see ensureWeekSession).
 */
export async function saveWeekSessionAsMentor(studentId, mentorId, weekOf, updates) {
  await updateDoc(
    doc(db, "sessions", sessionDocId(studentId, mentorId, weekOf)),
    { ...updates, updatedAt: serverTimestamp() }
  );
}

/**
 * All of a mentor's session docs for a given week, one query instead of one read per
 * student — used by /mentor/ to show "La Semana" per discípulo.
 */
export async function getMentorWeekSessions(mentorId, weekOf) {
  const q = query(collection(db, "sessions"), where("mentorId", "==", mentorId), where("weekOf", "==", weekOf));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
